import React from 'react';

import { Save, X, Upload, FileText, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import PhoneInput, { parsePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import '../../../../../styles/components/action-modal.scss';
import '../../../../../styles/components/withdrawal-action-modal.scss';

import i18n from '../../../../../i18n';
import { createOrganization, updateOrganization } from '../services/organizationServices';
import { CustomModal } from '../../../../common/CustomModal';
import { imageUrl } from '../../../../../constants/global';
import { getMzadcomAccountList } from '../../PaymentBankAccount/services/bankAccountServices';

const EMPTY_FORM = {
  organization_name: '',
  organization_name_ar: '',
  description: '',
  description_ar: '',
  is_client: true,
  username: '',
  password: '',
  email: '',
  mobile: '', // full E.164 string from PhoneInput, e.g. "+96891234567"
  default_mzadcom_bank_account_id: '',
};

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2 MB, matches backend `image|max:2048`
const IMAGE_INPUT_ID = 'org-file-organization-image';
const DEFAULT_PHONE_COUNTRY = 'OM';

export class OrganizationModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      form: { ...EMPTY_FORM },

      // Image upload state, mirroring WithdrawalActionModal's file-field handling.
      file_organization_image: null,
      imagePreviewUrl: null,
      dragOver: false,

      // Mzadcom accounts for the "default bank account" dropdown
      mzadcomAccounts: [],
      mzadcomLoading: false,

      errors: {},
      submitting: false,
    };

    this.imageFieldRef = React.createRef();
  }

  componentDidMount() {
    this.fetchMzadcomAccounts();
  }

  componentDidUpdate(prevProps) {
    // Hydrate form whenever the modal is opened, either for create or edit.
    if (this.props.show && !prevProps.show) {
      this.revokePreviewUrl();

      const { organization } = this.props;

      this.setState({
        form: organization
          ? {
              organization_name: organization.organization_name || '',
              organization_name_ar: organization.organization_name_ar || '',
              description: organization.description || '',
              description_ar: organization.description_ar || '',
              is_client: !!organization.is_client,
              username: organization.user_deatils?.username || '',
              password: '',
              email: organization.user_deatils?.email || '',
              // Existing records store country_code + mobile separately;
              // combine them into the E.164 string PhoneInput expects.
              mobile: organization.user_deatils?.mobile
                ? `${organization.user_deatils?.country_code || '+968'}${organization.user_deatils.mobile}`
                : '',
              default_mzadcom_bank_account_id: organization.default_mzadcom_bank_account_id
                ? String(organization.default_mzadcom_bank_account_id)
                : '',
            }
          : { ...EMPTY_FORM },
        file_organization_image: organization?.file_organization_image || null,
        imagePreviewUrl: null,
        dragOver: false,
        errors: {},
        submitting: false,
      });
    }
  }

  componentWillUnmount() {
    this.revokePreviewUrl();
  }

  /* ----------------------------------------------------------------------
   * Mzadcom bank accounts — for the "default account" dropdown
   * -------------------------------------------------------------------- */

  fetchMzadcomAccounts = async () => {
    this.setState({ mzadcomLoading: true });

    try {
      const data = await getMzadcomAccountList();

      if (data.success) {
        const accounts = (data.data || []).filter((account) => account.is_active);
        this.setState({ mzadcomAccounts: accounts });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.setState({ mzadcomLoading: false });
    }
  };

  revokePreviewUrl = () => {
    if (this.state.imagePreviewUrl) {
      URL.revokeObjectURL(this.state.imagePreviewUrl);
    }
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      form: { ...prevState.form, [name]: value },
      errors: { ...prevState.errors, [name]: undefined },
    }));
  };

  handlePhoneChange = (value) => {
    this.setState((prevState) => ({
      form: { ...prevState.form, mobile: value || '' },
      errors: { ...prevState.errors, mobile: undefined },
    }));
  };

  toggleClient = () => {
    this.setState((prevState) => ({
      form: { ...prevState.form, is_client: !prevState.form.is_client },
    }));
  };

  formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  validateImageFile = (file) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return i18n.t('invalid_file_type') || 'Only JPG, PNG or WEBP images are allowed';
    }
    if (file.size > MAX_IMAGE_SIZE) {
      return i18n.t('file_too_large') || 'File size must not exceed 2 MB';
    }
    return '';
  };

  setImageFile = (file) => {
    if (!file) return;

    const error = this.validateImageFile(file);

    if (error) {
      this.setState((prevState) => ({
        errors: { ...prevState.errors, file_organization_image: error },
      }));
      return;
    }

    this.revokePreviewUrl();

    this.setState((prevState) => ({
      file_organization_image: file,
      imagePreviewUrl: URL.createObjectURL(file),
      errors: { ...prevState.errors, file_organization_image: undefined },
    }));
  };

  handleImageInputChange = (e) => {
    const file = e.target.files?.[0] || null;
    this.setImageFile(file);
    e.target.value = '';
  };

  handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (this.state.submitting) return;
    this.setState({ dragOver: true });
  };

  handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ dragOver: false });
  };

  handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ dragOver: false });
    if (this.state.submitting) return;
    const file = e.dataTransfer.files?.[0] || null;
    this.setImageFile(file);
  };

  removeImage = () => {
    this.revokePreviewUrl();
    this.setState((prevState) => ({
      file_organization_image: null,
      imagePreviewUrl: null,
      errors: { ...prevState.errors, file_organization_image: undefined },
    }));
  };

  validate = () => {
    const { form, file_organization_image } = this.state;
    const { organization } = this.props;
    const isEdit = !!organization;
    const errors = {};

    if (!form.organization_name.trim()) errors.organization_name = i18n.t('field_required');
    if (!form.organization_name_ar.trim()) errors.organization_name_ar = i18n.t('field_required');
    if (!form.description.trim()) errors.description = i18n.t('field_required');
    if (!form.description_ar.trim()) errors.description_ar = i18n.t('field_required');

    if (!isEdit && !file_organization_image) {
      errors.file_organization_image = i18n.t('field_required');
    }

    if (!form.username.trim()) errors.username = i18n.t('field_required');
    if (!isEdit && !form.password.trim()) errors.password = i18n.t('field_required');
    if (!form.email.trim()) errors.email = i18n.t('field_required');

    if (!form.mobile.trim()) {
      errors.mobile = i18n.t('field_required');
    } else {
      const parsed = parsePhoneNumber(form.mobile);
      if (!parsed || !parsed.isValid()) {
        errors.mobile = i18n.t('invalid_mobile_number') || 'Enter a valid mobile number';
      }
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmit = async () => {
    if (!this.validate()) return;

    const { organization, onSaved } = this.props;
    const { form, file_organization_image } = this.state;
    const isEdit = !!organization;

    const formData = new FormData();
    formData.append('organization_name', form.organization_name);
    formData.append('organization_name_ar', form.organization_name_ar);
    formData.append('description', form.description);
    formData.append('description_ar', form.description_ar);
    formData.append('is_client', form.is_client ? 1 : 0);

    if (form.default_mzadcom_bank_account_id) {
      formData.append('default_mzadcom_bank_account_id', form.default_mzadcom_bank_account_id);
    }

    formData.append('username', form.username);
    formData.append('email', form.email);
    if (form.password) {
      formData.append('password', form.password);
    }

    // Split the E.164 value PhoneInput gives us into the country_code +
    // national number pair your backend already stores separately.
    const parsed = parsePhoneNumber(form.mobile);
    formData.append('country_code', `+${parsed.countryCallingCode}`);
    formData.append('mobile', parsed.nationalNumber);

    if (file_organization_image instanceof File) {
      formData.append('file_organization_image', file_organization_image);
    }

    this.setState({ submitting: true });

    try {
      const response = isEdit
        ? await updateOrganization(organization.id, formData)
        : await createOrganization(formData);

      if (response && response.success) {
        toast.success(response.message || (isEdit ? i18n.t('updated_successfully') : i18n.t('created_successfully')));
        onSaved?.(response.data);
        this.handleClose();
      } else {
        const rawErrors = response?.errors?.[0] || {};
        // Flatten each field's error array down to its first message string,
        // since the form only renders a single message per field.
        const apiErrors = Object.fromEntries(
          Object.entries(rawErrors).map(([field, messages]) => [
            field,
            Array.isArray(messages) ? messages[0] : messages,
          ]),
        );

        this.setState((prevState) => ({
          errors: { ...prevState.errors, ...apiErrors },
        }));
        toast.error(response?.message || i18n.t('something_went_wrong'));
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.message || i18n.t('something_went_wrong'));
      const rawErrors = error?.errors?.[0] || {};
      // Flatten each field's error array down to its first message string,
      // since the form only renders a single message per field.
      const apiErrors = Object.fromEntries(
        Object.entries(rawErrors).map(([field, messages]) => [field, Array.isArray(messages) ? messages[0] : messages]),
      );

      this.setState((prevState) => ({
        errors: { ...prevState.errors, ...apiErrors },
      }));
      toast.error(error?.message || i18n.t('something_went_wrong'));
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleClose = () => {
    this.revokePreviewUrl();
    this.setState({
      form: { ...EMPTY_FORM },
      file_organization_image: null,
      imagePreviewUrl: null,
      dragOver: false,
      errors: {},
      submitting: false,
    });
    this.props.onClose();
  };

  renderImageUploadField() {
    const { submitting, errors, dragOver, file_organization_image, imagePreviewUrl } = this.state;
    const { organization } = this.props;
    const isEdit = !!organization;

    const isNewFile = file_organization_image instanceof File;
    const fileName = isNewFile ? file_organization_image.name : file_organization_image;
    const fileSize = isNewFile ? file_organization_image.size : null;

    const previewUrl = isNewFile
      ? imagePreviewUrl
      : file_organization_image
        ? `${imageUrl}uploads/organization/${file_organization_image}`
        : null;

    return (
      <div className="wam-form-group wam-form-group--file">
        <label className="wam-form-label" htmlFor={IMAGE_INPUT_ID}>
          {i18n.t('organization_image')}
          {!isEdit && <span className="wam-required-mark">*</span>}
        </label>

        <input
          type="file"
          id={IMAGE_INPUT_ID}
          name="file_organization_image"
          accept=".jpg,.jpeg,.png,.webp"
          style={{ display: 'none' }}
          disabled={submitting}
          onChange={this.handleImageInputChange}
        />

        {!file_organization_image ? (
          <div
            className={`wam-upload-zone ${dragOver ? 'wam-upload-zone--dragover' : ''} ${
              submitting ? 'wam-upload-zone--disabled' : ''
            } ${errors.file_organization_image ? 'wam-upload-zone--error' : ''}`}
            onDragOver={this.handleDragOver}
            onDragLeave={this.handleDragLeave}
            onDrop={this.handleDrop}
          >
            <div className="wam-upload-zone__icon">
              <Upload size={24} />
            </div>
            <p className="wam-upload-zone__title">{i18n.t('drop_file_here') || 'Drop image here'}</p>
            <p className="wam-upload-zone__sub">
              {i18n.t('organization_image_hint') || 'JPG, PNG or WEBP — 200×200px recommended, max 2 MB'}
            </p>
            <button
              type="button"
              ref={this.imageFieldRef}
              className="wam-upload-zone__btn"
              disabled={submitting}
              onClick={() => document.getElementById(IMAGE_INPUT_ID).click()}
            >
              {i18n.t('browse_file') || 'Browse File'}
            </button>
          </div>
        ) : (
          <div className="wam-receipt-preview">
            <div className="wam-receipt-preview__header">
              <FileText size={15} className="wam-receipt-preview__file-icon" />
              <span className="wam-receipt-preview__name">{fileName}</span>
              <span className="wam-receipt-preview__size">
                {isNewFile ? this.formatFileSize(fileSize) : i18n.t('current_image') || 'Current image'}
              </span>
              <button
                type="button"
                className="wam-receipt-preview__remove"
                onClick={this.removeImage}
                disabled={submitting}
              >
                <X size={12} /> {i18n.t('remove') || 'Remove'}
              </button>
            </div>

            <div className="wam-receipt-preview__body wam-receipt-preview__body--image">
              {previewUrl && <img src={previewUrl} alt="" className="wam-receipt-preview__img" />}
            </div>

            <div className="wam-receipt-preview__actions">
              <button
                type="button"
                className="wam-receipt-preview__change-btn"
                onClick={() => document.getElementById(IMAGE_INPUT_ID).click()}
                disabled={submitting}
              >
                <Upload size={13} /> {i18n.t('change_file') || 'Change File'}
              </button>
            </div>
          </div>
        )}

        {errors.file_organization_image && (
          <div id={`${IMAGE_INPUT_ID}-error`} className="wam-upload-error">
            <AlertCircle size={13} />
            {errors.file_organization_image}
          </div>
        )}
      </div>
    );
  }

  render() {
    const { show, organization } = this.props;
    const { form, errors, submitting, mzadcomAccounts, mzadcomLoading } = this.state;
    const isEdit = !!organization;

    return (
      <CustomModal
        isOpen={show}
        header={isEdit ? i18n.t('edit_organization') : i18n.t('create_organization')}
        onClose={this.handleClose}
      >
        <div className="action-modal-container">
          <div className="payment-details-card">
            <div className="details-grid">
              <div className="detail-item-new">
                <label className="reject-label">
                  {i18n.t('title')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="organization_name"
                  className={`reject-textarea ${errors.organization_name ? 'error' : ''}`}
                  value={form.organization_name}
                  onChange={this.handleChange}
                  disabled={submitting}
                />
                {errors.organization_name && <span className="error-message">{errors.organization_name}</span>}
              </div>

              <div className="detail-item-new">
                <label className="reject-label">
                  {i18n.t('title_arabic')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="organization_name_ar"
                  className={`reject-textarea rtl-dir ${errors.organization_name_ar ? 'error' : ''}`}
                  value={form.organization_name_ar}
                  onChange={this.handleChange}
                  disabled={submitting}
                />
                {errors.organization_name_ar && <span className="error-message">{errors.organization_name_ar}</span>}
              </div>

              <div className="detail-item-new">
                <label className="reject-label">
                  {i18n.t('description')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="description"
                  className={`reject-textarea ${errors.description ? 'error' : ''}`}
                  value={form.description}
                  onChange={this.handleChange}
                  disabled={submitting}
                />
                {errors.description && <span className="error-message">{errors.description}</span>}
              </div>

              <div className="detail-item-new">
                <label className="reject-label">
                  {i18n.t('description_arabic')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="description_ar"
                  className={`reject-textarea rtl-dir ${errors.description_ar ? 'error' : ''}`}
                  value={form.description_ar}
                  onChange={this.handleChange}
                  disabled={submitting}
                />
                {errors.description_ar && <span className="error-message">{errors.description_ar}</span>}
              </div>

              {/* Default Mzadcom bank account */}
              <div className="detail-item-new full-width">
                <label className="reject-label">{i18n.t('default_mzadcom_bank_account')}</label>
                <select
                  name="default_mzadcom_bank_account_id"
                  className="reject-textarea"
                  value={form.default_mzadcom_bank_account_id}
                  onChange={this.handleChange}
                  disabled={submitting || mzadcomLoading}
                >
                  <option value="">
                    {mzadcomLoading
                      ? i18n.t('loading') || 'Loading...'
                      : i18n.t('no_default_account') || '-- No default account --'}
                  </option>
                  {mzadcomAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.bank_name} — {account.account_number}
                      {account.is_default ? ` (${i18n.t('primary') || 'Primary'})` : ''}
                    </option>
                  ))}
                </select>
                <small className="field-hint">
                  {i18n.t('default_mzadcom_bank_account_hint') ||
                    "The Mzadcom bank account this organization's payments default to."}
                </small>
              </div>

              <div className="detail-item-new full-width">{this.renderImageUploadField()}</div>

              <div className="detail-item-new full-width">
                <label className="reject-label checkbox-label">
                  <input
                    type="checkbox"
                    name="is_client"
                    checked={form.is_client}
                    onChange={this.toggleClient}
                    disabled={submitting}
                  />
                  {i18n.t('is_client')}
                </label>
              </div>

              <div className="detail-item-new full-width client-credentials-section">
                <div className="details-grid">
                  <div className="detail-item-new">
                    <label className="reject-label">
                      {i18n.t('username')} <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="username"
                      className={`reject-textarea ${errors.username ? 'error' : ''}`}
                      value={form.username}
                      onChange={this.handleChange}
                      disabled={submitting}
                    />
                    {errors.username && <span className="error-message">{errors.username}</span>}
                  </div>

                  <div className="detail-item-new">
                    <label className="reject-label">
                      {i18n.t('password')}
                      {!isEdit && <span className="required">*</span>}
                    </label>
                    <input
                      type="password"
                      name="password"
                      className={`reject-textarea ${errors.password ? 'error' : ''}`}
                      value={form.password}
                      onChange={this.handleChange}
                      disabled={submitting}
                      placeholder={isEdit ? i18n.t('leave_blank_to_keep_current') : ''}
                    />
                    {errors.password && <span className="error-message">{errors.password}</span>}
                  </div>

                  <div className="detail-item-new">
                    <label className="reject-label">
                      {i18n.t('email')} <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="email"
                      className={`reject-textarea ${errors.email ? 'error' : ''}`}
                      value={form.email}
                      onChange={this.handleChange}
                      disabled={submitting}
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>

                  <div className="detail-item-new">
                    <label className="reject-label">
                      {i18n.t('mobile')} <span className="required">*</span>
                    </label>
                    <PhoneInput
                      international
                      defaultCountry={DEFAULT_PHONE_COUNTRY}
                      value={form.mobile}
                      onChange={this.handlePhoneChange}
                      disabled={submitting}
                      className={`phone-input-field ${errors.mobile ? 'error' : ''}`}
                    />
                    {errors.mobile && <span className="error-message">{errors.mobile}</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="action-buttons-container">
            <button className="modal-btn btn-cancel" onClick={this.handleClose} disabled={submitting}>
              <X size={18} />
              {i18n.t('cancel')}
            </button>

            <button className="modal-btn btn-success" onClick={this.handleSubmit} disabled={submitting}>
              <Save size={18} />
              {submitting ? i18n.t('saving') : i18n.t('save')}
            </button>
          </div>
        </div>
      </CustomModal>
    );
  }
}

export default OrganizationModal;
